import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import api from '../../utils/api';
import { useCartCtx } from '../../App';
import { useCurrency } from '../../hooks/useCurrency';

import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import CrossfadeHero from '../../components/storefront/CrossfadeHero';
import ScrollProgress from '../../components/storefront/ScrollProgress';
import BubbleAbacus, { ZoneAbacus } from '../../components/storefront/BubbleAbacus';

import styles from './BubbleProduct.module.css';

// Refined thin-line icons replace the original emoji glyphs — monochrome,
// stroke-only marks that scale with their container's font-size (width/height
// in `em`) and inherit color via `currentColor`, so a single node can render
// small on a bead and large in the zone panel, and flip white when its bead
// goes active. Kept inline (no icon dependency) to match the page's port.
function LineIcon({ name }) {
  const p = {
    width: '1em',
    height: '1em',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    style: { display: 'block' },
  };
  switch (name) {
    case 'collagen': // a plump pearl with a catch-light
      return (<svg {...p}><circle cx="12" cy="12" r="7.5" /><path d="M8.2 8.6a5 5 0 0 1 3-1.4" /></svg>);
    case 'hydration': // droplet
      return (<svg {...p}><path d="M12 3.2C12 3.2 5.5 10 5.5 14.8a6.5 6.5 0 0 0 13 0C18.5 10 12 3.2 12 3.2Z" /></svg>);
    case 'smoothing': // a wrinkle pressed smooth between two even lines
      return (<svg {...p}><path d="M4 8h16" /><path d="M4 12c4-3 12-3 16 0" /><path d="M4 16h16" /></svg>);
    case 'glow': // four-point sparkle
      return (<svg {...p}><path d="M12 3l1.9 6.1L20 11l-6.1 1.9L12 19l-1.9-6.1L4 11l6.1-1.9z" /></svg>);
    case 'clean': // leaf
      return (<svg {...p}><path d="M12 4C7 8.2 7 15.2 12 20C17 15.2 17 8.2 12 4Z" /><path d="M12 6.2v11.6" /></svg>);
    case 'swipe': // a swept balm trail with a tip flourish
      return (<svg {...p}><path d="M4 18C9 17 14 12.5 19.5 5.5" /><path d="M15.6 5.5h4v4" /></svg>);
    case 'face':
      return (<svg {...p}><circle cx="12" cy="12" r="8.2" /><path d="M9 10.6h.01M15 10.6h.01" /><path d="M9.3 14.6c1.6 1.3 3.8 1.3 5.4 0" /></svg>);
    case 'lips':
      return (<svg {...p}><path d="M3 12c2.2-2.8 5-2.8 9-2.8s6.8 0 9 2.8c-2.2 2.8-5 3.8-9 3.8s-6.8-1-9-3.8Z" /><path d="M3 12h18" /></svg>);
    case 'cheeks': // a face with two soft blush sweeps
      return (<svg {...p}><circle cx="12" cy="12" r="8.2" /><path d="M6.6 13c.8.7 2.1.7 2.9 0" /><path d="M14.5 13c.8.7 2.1.7 2.9 0" /></svg>);
    case 'eyes':
      return (<svg {...p}><path d="M2.5 12S6.5 6 12 6s9.5 6 9.5 6-4 6-9.5 6S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.6" /></svg>);
    case 'dryspots': // sparkle
      return (<svg {...p}><path d="M12 3l1.9 6.1L20 11l-6.1 1.9L12 19l-1.9-6.1L4 11l6.1-1.9z" /></svg>);
    case 'bloom': // a radiant daisy flourish for the intro
      return (<svg {...p}><circle cx="12" cy="12" r="2.6" /><path d="M12 9.4V3.8M12 14.6V20.2M9.4 12H3.8M14.6 12H20.2M10.1 10.1L6.2 6.2M13.9 10.1l3.9-3.9M10.1 13.9l-3.9 3.9M13.9 13.9l3.9 3.9" /></svg>);
    case 'heart': // a soft line heart for the intro
      return (<svg {...p}><path d="M12 20.2s-7.2-4.6-7.2-10A4.2 4.2 0 0 1 12 7.1a4.2 4.2 0 0 1 7.2 3.1c0 5.4-7.2 10-7.2 10Z" /></svg>);
    default:
      return null;
  }
}

// Ported from pink.html's `benefits` array. Mapped to BenefitCards' shape
// ({ icon, title, summary, more }).
const BENEFITS = [
  {
    icon: <LineIcon name="collagen" />,
    title: 'Volumizing Collagen',
    summary: 'Tap to see how it plumps',
    more: "Marine-derived collagen peptides cushion the skin's surface, visibly plumping fine lines and giving cheeks and lips a soft, bouncy fullness.",
  },
  {
    icon: <LineIcon name="hydration" />,
    title: 'Deep Hydration',
    summary: 'Tap for the moisture story',
    more: 'A balm-to-skin melt of hyaluronic acid and squalane floods thirsty skin with up to 24 hours of cushiony, non-greasy moisture.',
  },
  {
    icon: <LineIcon name="smoothing" />,
    title: 'Wrinkle Smoothing',
    summary: 'Tap to smooth things over',
    more: "Peptides and vitamin E soften the look of crow's feet, smile lines and dry creases, leaving a blurred, pillow-soft finish.",
  },
  {
    icon: <LineIcon name="glow" />,
    title: 'Pillowy Glow',
    summary: 'Tap for that dewy finish',
    more: 'A sheer rosy tint and dewy veil give an instant lit-from-within flush — beautiful bare or layered over makeup.',
  },
  {
    icon: <LineIcon name="clean" />,
    title: 'Gentle & Clean',
    summary: 'Tap for the clean list',
    more: 'Fragrance-light, non-comedogenic, and free of parabens and sulfates — kind enough for lips, lids and sensitive cheeks.',
  },
  {
    icon: <LineIcon name="swipe" />,
    title: 'Swipe-Anywhere',
    summary: 'Tap to see the zones',
    more: 'One twist-up stick replaces five products. No mirror, no mess — glide it on face, lips, cheeks, lids or any dry spot.',
  },
];

// Ported from pink.html's `uses` array (multi-use toggle).
const USES = [
  { k: 'Face', e: <LineIcon name="face" />, t: 'On the face', p: 'Smooth over cheeks, forehead and jaw for an all-over hydrated, soft-focus glow that sits beautifully under or over makeup.' },
  { k: 'Lips', e: <LineIcon name="lips" />, t: 'On the lips', p: 'A nourishing balm that conditions and plumps lips with a sheer pink wash of color and serious cushiony comfort.' },
  { k: 'Cheeks', e: <LineIcon name="cheeks" />, t: 'On the cheeks', p: "Dab and blend high on the cheekbones for a dewy, blushing flush that looks like it's coming from within." },
  { k: 'Eyes', e: <LineIcon name="eyes" />, t: 'On the lids & under-eyes', p: 'Press a thin veil onto lids and the under-eye to soften dry, crepey texture and add a soft luminous sheen.' },
  { k: 'Dry Spots', e: <LineIcon name="dryspots" />, t: 'On dry spots', p: 'Rescue cuticles, elbows and any tight, flaky patch with a quick swipe of collagen-rich moisture, anytime.' },
];

const STATS = [
  { num: '+92%', lbl: 'saw plumper, smoother skin in 2 weeks*' },
  { num: '24h', lbl: "cushiony hydration that won't quit" },
  { num: '5-in-1', lbl: 'face, lips, cheeks, lids & dry spots' },
  { num: '0', lbl: 'parabens, sulfates & sticky residue' },
];

const RITUAL = [
  { n: 1, h: 'Swipe', p: 'Glide the warm balm directly onto clean skin, lips, or anywhere feeling tight.' },
  { n: 2, h: 'Press', p: 'Pat with fingertips to melt the collagen veil in and lock the moisture down.' },
  { n: 3, h: 'Glow', p: 'Layer under or over makeup all day for a plush, dewy, pillow-soft finish.' },
];

// Decorative floating bubbles (ported from the inline bubble-generator script).
const BUBBLES = Array.from({ length: 22 }, (_, i) => {
  const size = 14 + Math.random() * 70;
  return {
    width: size,
    left: Math.random() * 100,
    duration: 9 + Math.random() * 12,
    delay: -Math.random() * 15,
    key: i,
  };
});

export default function BubbleProduct() {
  const { addItem } = useCartCtx();
  const { fmt } = useCurrency();

  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeUse, setActiveUse] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get('/products/bubble').then((r) => {
      if (!alive) return;
      setProduct(r.data);
      setVariant(r.data.variants[0]);
    });
    return () => { alive = false; };
  }, []);

  const onAdd = () => {
    if (!variant || !product) return;
    addItem(variant, product, qty);
    toast.success('Added to bag');
  };

  if (!product || !variant) {
    return (
      <div className={styles.page}>
        <Navbar />
        <div className={styles.loading}>Loading…</div>
        <Footer />
      </div>
    );
  }

  const showCompare = variant.comparePriceInCents > variant.priceInCents;

  return (
    <div className={styles.page}>
      <ScrollProgress color="linear-gradient(90deg,#ff9ec9,#e0468f)" height={4} />

      <div className={styles.bubbles} aria-hidden="true">
        {BUBBLES.map((b) => (
          <div
            key={b.key}
            className={styles.bubble}
            style={{
              width: `${b.width}px`,
              height: `${b.width}px`,
              left: `${b.left}vw`,
              animationDuration: `${b.duration}s`,
              animationDelay: `${b.delay}s`,
            }}
          />
        ))}
      </div>

      <Navbar />

      {/* ============ HERO ============ */}
      <CrossfadeHero src="/videos/pink-loop.mp4" overlay="rgba(0,0,0,.15)" className={styles.hero}>
        <div className={styles.heroVeil} />
        <div className={styles.heroInner}>
          <span className={styles.kicker}>Pink Collagen Volume</span>
          <h1>
            Plump. Glow.
            <em>Multi Balm Stick for Face &amp; Lips</em>
          </h1>
          <p>
            One blushing stick that volumizes, smooths fine lines, and floods skin with
            collagen-rich hydration. Swipe it anywhere you want a dewy, pillow-soft finish.
          </p>
          <div className={styles.heroCta}>
            <a className={styles.btnPrimary} href="#buy">Shop {fmt(variant.priceInCents)}</a>
            <a className={styles.btnGhost} href="#benefits">Why it works</a>
          </div>
        </div>
        <div className={styles.scrollHint}>Scroll ↓</div>
      </CrossfadeHero>

      {/* ============ INTRO ============ */}
      <section className={styles.intro}>
        <div className={styles.wrap}>
          <div className={styles.stick}><LineIcon name="heart" /></div>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>Meet the stick</div>
            <h2>A pink bubble dream in your pocket</h2>
            <p>
              {product.description ||
                'The Pink Collagen Volume Multi Balm Stick melts into skin like whipped marshmallow, leaving a cushiony veil of moisture and a soft rosy glow. No mirror, no mess — just glide and go.'}
            </p>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS + STATS — the bubble abacus ============ */}
      <section className={styles.benefits} id="benefits">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>The benefits</div>
            <h2>Count the reasons to swipe</h2>
            <p>The proof tallies up, then slide each bubble bead across to unfold the science behind the softness.</p>
          </div>
          <BubbleAbacus benefits={BENEFITS} stats={STATS} />
          <p className={styles.statNote}>*Based on a consumer-use study. Individual results vary.</p>
        </div>
      </section>

      {/* ============ MULTI-USE — the zone rod ============ */}
      <section className={styles.uses} id="uses">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>One stick, everywhere</div>
            <h2>Truly multi-use</h2>
            <p>Slide along the rod — see how the balm works in each zone.</p>
          </div>
          <ZoneAbacus uses={USES} active={activeUse} onChange={setActiveUse} />
        </div>
      </section>

      {/* ============ RITUAL ============ */}
      <section className={styles.ritual} id="ritual">
        <div className={styles.wrap}>
          <div className={styles.secHead}>
            <div className={styles.eyebrow}>The ritual</div>
            <h2>Three swipes to a bubble glow</h2>
          </div>
          <div className={styles.steps}>
            {RITUAL.map((r) => (
              <div className={styles.rstep} key={r.n}>
                <div className={styles.rn}>{r.n}</div>
                <h4>{r.h}</h4>
                <p>{r.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BUY ============ */}
      <section className={styles.buy} id="buy">
        <div className={styles.wrap}>
          <div className={styles.buyCard}>
            <div className={styles.eyebrow}>Bestseller</div>
            <h3>{product.name}</h3>
            <div className={styles.sub}>Wrinkle &amp; hydrating stick for face &amp; lips</div>
            <div className={styles.price}>
              {fmt(variant.priceInCents)}
              {showCompare && <small>{fmt(variant.comparePriceInCents)}</small>}
            </div>
            <div className={styles.qtyrow}>
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">−</button>
              <span>{qty}</span>
              <button type="button" onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity">+</button>
            </div>
            <button type="button" className={`${styles.btnPrimary} ${styles.buyAdd}`} onClick={onAdd}>
              Add to Bag
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
